import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Clock, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Check, FileText, ClipboardList } from 'lucide-react';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { getExamById } from '../utils/examAPI';
import { getSubjectById, getSubjectQuestionConfig } from '../utils/subjectsAPI';
import { addSubjectHistory } from '../utils/subjectHistorysAPI';
import { addMistakesBatch } from '../utils/mistakesAPI';

// 生成UUID的函数
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

// 添加题型名称处理函数
const getTypeName = (type) => {
  switch(type) {
    case '1': return '单选题';
    case '2': return '判断题';
    case '3': return '多选题';
    default: return `${type}`;
  }
};

// 获取题型样式函数 - 用于设置题型标签的样式
// 注意：此函数在其他组件中可能会被使用，保留以便将来扩展
// eslint-disable-next-line no-unused-vars
const getTypeStyle = (type) => {
  switch(type) {
    case '1': return 'bg-blue-50 text-blue-600 border-blue-200';
    case '2': return 'bg-green-50 text-green-600 border-green-200';
    case '3': return 'bg-purple-50 text-purple-600 border-purple-200';
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

function SubjectExam({ session }) {
  const { examId, subjectId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const [exam, setExam] = useState(null);
  const [subject, setSubject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(50 * 60); // 默认50分钟，单位秒
  const [initialTime, setInitialTime] = useState(50 * 60); // 保存初始总时间以计算进度
  const [loading, setLoading] = useState(true);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [questionTimes, setQuestionTimes] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const navigate = useNavigate();

  // 格式化剩余时间，改回使用分钟:秒格式便于阅读
  const formatTime = useCallback((seconds) => {
    if (seconds <= 0) return "00:00";
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    // 显示分钟:秒格式
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // 获取计时器颜色，根据剩余时间变化
  const getTimerColor = useCallback((seconds) => {
    if (seconds < 60) return 'text-red-600 font-bold animate-pulse'; // 不到1分钟，红色闪烁
    if (seconds < 300) return 'text-red-500 font-bold'; // 不到5分钟，红色
    if (seconds < 600) return 'text-yellow-500 font-bold'; // 不到10分钟，黄色
    return 'text-primary'; // 正常颜色
  }, []);

  // 计算剩余时间比例
  const getTimeProgress = useCallback(() => {
    if (initialTime <= 0) return 0;
    return Math.max(0, Math.min(100, (timeLeft / initialTime) * 100));
  }, [timeLeft, initialTime]);

  // 提交考试答案
  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      
      // 计算得分
      let score = 0;
      let correctCount = 0;
      
      questions.forEach(question => {
        const questionId = getQuestionId(question);
        const userAnswer = answers[questionId] || '';
        const correctOption = question.options.find(opt => opt.value === "1");
        
        if (correctOption && userAnswer === correctOption.code) {
          correctCount++;
        }
      });
      
      // 满分100分
      score = Math.round((correctCount / questions.length) * 100);
      
      // 保存考试记录
      const userId = session?.user?.id || localStorage.getItem('user_id');
      
      if (userId) {
        // 使用API函数替代直接数据库操作
        await addSubjectHistory({
          id: generateUUID(), // 生成唯一ID
          user_id: userId,
          exam_id: examId,
          subject_id: subjectId,
          score: score,
          answers: answers,
          question_times: questionTimes,
          created_at: new Date().toISOString(),
          total_questions: questions.length,
          correct_count: correctCount
        });
        
        // 找出做错的题目
        const wrongQuestions = questions.filter(question => {
          const questionId = getQuestionId(question);
          const userAnswer = answers[questionId] || '';
          const correctOption = question.options.find(opt => opt.value === "1");
          return correctOption && userAnswer !== correctOption.code && userAnswer !== ''; // 只保存已作答的错题
        });
        
        // 批量保存错题
        if (wrongQuestions.length > 0) {
          // 添加考试和科目信息到每个错题
          const enhancedWrongQuestions = wrongQuestions.map(q => ({
            ...q,
            examid: Number(examId),
            subjectId: Number(subjectId),
            examName: exam?.name || `考试${examId}`,
            subjectName: subject?.name || `科目${subjectId}`
          }));
          
          const result = await addMistakesBatch(userId, enhancedWrongQuestions, answers);
          console.log(`批量保存了 ${result.savedCount} 道错题到错题本`);
        } else {
          console.log('没有错题需要保存');
        }
      }
      
      // 重定向到结果页面
      navigate(`/subject-exam-result/${examId}/${subjectId}`, {
        state: {
          score,
          answers,
          questionTimes,
          questions,
          subject,
          totalTime: (subject?.duration || 50) * 60 - timeLeft,
          correct_count: correctCount
        }
      });
      
    } catch (error) {
      console.error('Error submitting exam:', error.message);
    } finally {
      setLoading(false);
    }
  }, [answers, examId, subjectId, navigate, questions, questionTimes, session, timeLeft, subject, exam]);

  useEffect(() => {
    const fetchSubjectQuestions = async () => {
      try {
        setLoading(true);
        
        // 检查用户身份
        if (!session && !localStorage.getItem('user_id')) {
          navigate('/login');
          return;
        }
        
        // 获取考试详情
        const examData = await getExamById(examId);
        if (!examData) throw new Error('找不到考试信息');
        setExam(examData);
        
        // 获取科目信息
        const subjectData = await getSubjectById(subjectId);
        if (!subjectData) throw new Error('找不到科目信息');
        setSubject(subjectData);
        
        // 设置考试时间
        if (subjectData && subjectData.duration) {
          const durationInSeconds = subjectData.duration * 60; // 转换为秒
          setTimeLeft(durationInSeconds);
          setInitialTime(durationInSeconds); // 设置初始总时间
        }
        
        // 获取科目下的题目并抽取
        let selectedQuestions = [];
        let totalExpectedQuestions = 0;
        
        // 获取科目题型配置
        const subjectQuestions = await getSubjectQuestionConfig(subjectId);
        
        // 判断是否有科目题型配置
        if (subjectQuestions && subjectQuestions.length > 0) {
          console.log('使用subject_question表配置抽题, 共有配置项:', subjectQuestions.length);
          
          // 计算预期总题目数
          totalExpectedQuestions = subjectQuestions.reduce((sum, sq) => sum + sq.question_count, 0);
          console.log('预期总题目数:', totalExpectedQuestions);
          
          // 创建已抽取题目ID集合，防止重复抽题
          const selectedQuestionIds = new Set();
          
          // 按题型抽取题目
          for (const sq of subjectQuestions) {
            const questionTypeId = sq.question_type_id;
            const count = sq.question_count;
            
            console.log(`抽取题型${questionTypeId}的题目，数量:${count}`);
            
            // 转换题型ID为typename值
            let typenameValue;
            switch(questionTypeId) {
              case 1: typenameValue = '单选'; break;
              case 2: typenameValue = '判断'; break;
              case 3: typenameValue = '多选'; break;
              default: typenameValue = questionTypeId.toString();
            }
            
            // 查询指定题型的题目
            const questionsData = await getQuestionsByExamId(subjectId, true);
            const typeQuestions = questionsData.filter(q => q.typename === typenameValue);
            
            if (typeQuestions && typeQuestions.length > 0) {
              console.log(`题型${questionTypeId}共有题目:${typeQuestions.length}题`);
              
              // 过滤掉已选题目
              const availableQuestions = typeQuestions.filter(q => !selectedQuestionIds.has(getQuestionId(q)));
              
              // 随机洗牌
              const shuffled = [...availableQuestions];
              for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
              }
              
              // 取需要数量的题目
              const selectedCount = Math.min(count, shuffled.length);
              const typeSelectedQuestions = shuffled.slice(0, selectedCount);
              
              // 添加已选题目ID
              typeSelectedQuestions.forEach(q => selectedQuestionIds.add(getQuestionId(q)));
              
              // 添加到总题目集
              selectedQuestions = [...selectedQuestions, ...typeSelectedQuestions];
              
              console.log(`已选题型${questionTypeId}的题目:${typeSelectedQuestions.length}题`);
            } else {
              console.log(`题型${questionTypeId}没有可用题目`);
            }
          }
          
          console.log(`最终选取题目:${selectedQuestions.length}/${totalExpectedQuestions}题`);
        } 
        // 兼容旧的type_count字段
        else if (subjectData.type_count) {
          let typeCountMap;
          try {
            // 尝试解析JSON字符串 (如果是字符串的话)
            typeCountMap = typeof subjectData.type_count === 'string' 
              ? JSON.parse(subjectData.type_count) 
              : subjectData.type_count;
              
            console.log('使用type_count字段配置抽题:', typeCountMap);
            
            // 计算预期总题目数
            totalExpectedQuestions = Object.values(typeCountMap).reduce((sum, count) => sum + count, 0);
            console.log('预期总题目数:', totalExpectedQuestions);
            
            // 创建已抽取题目ID集合，防止重复抽题
            const selectedQuestionIds = new Set();
            
            // 按题型抽取题目
            for (const [type, count] of Object.entries(typeCountMap)) {
              console.log(`抽取题型${type}的题目，数量:${count}`);
              
              // 转换题型ID为typename值
              let typenameValue;
              switch(type) {
                case '1': typenameValue = '单选'; break;
                case '2': typenameValue = '判断'; break;
                case '3': typenameValue = '多选'; break;
                default: typenameValue = type;
              }
              
              // 查询指定题型的题目
              const questionsData = await getQuestionsByExamId(subjectId, true);
              const typeQuestions = questionsData.filter(q => q.typename === typenameValue);
              
              if (typeQuestions && typeQuestions.length > 0) {
                console.log(`题型${type}共有题目:${typeQuestions.length}题`);
                
                // 过滤掉已选题目
                const availableQuestions = typeQuestions.filter(q => !selectedQuestionIds.has(getQuestionId(q)));
                
                // 随机洗牌
                const shuffled = [...availableQuestions];
                for (let i = shuffled.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                
                // 取需要数量的题目
                const selectedCount = Math.min(count, shuffled.length);
                const typeSelectedQuestions = shuffled.slice(0, selectedCount);
                
                // 添加已选题目ID
                typeSelectedQuestions.forEach(q => selectedQuestionIds.add(getQuestionId(q)));
                
                // 添加到总题目集
                selectedQuestions = [...selectedQuestions, ...typeSelectedQuestions];
                
                console.log(`已选题型${type}的题目:${typeSelectedQuestions.length}题`);
              } else {
                console.log(`题型${type}没有可用题目`);
              }
            }
            
            console.log(`最终选取题目:${selectedQuestions.length}/${totalExpectedQuestions}题`);
          } catch (error) {
            console.error('题型配置解析错误:', error);
            // 解析错误时使用默认抽题逻辑
            const allQuestions = await getQuestionsByExamId(subjectId, true);
            
            if (allQuestions && allQuestions.length > 0) {
              selectedQuestions = allQuestions;
            }
          }
        } else {
          // 使用原来的抽题逻辑
          const questionsData = await getQuestionsByExamId(subjectId, true);
          
          if (questionsData && questionsData.length > 0) {
            selectedQuestions = questionsData;
            
            // 如果科目有指定题目数量，且题库中的题目数量大于需要的数量，则随机抽取
            if (subjectData.count && questionsData.length > subjectData.count) {
              // 随机洗牌算法 (Fisher-Yates shuffle)
              const shuffled = [...questionsData];
              for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
              }
              
              // 取前count个题目
              selectedQuestions = shuffled.slice(0, subjectData.count);
              totalExpectedQuestions = subjectData.count;
            } else {
              totalExpectedQuestions = questionsData.length;
            }
          }
        }
        
        // 如果抽取的题目数不足预期，记录日志
        if (selectedQuestions.length < totalExpectedQuestions) {
          console.warn(`警告: 实际抽取题目数(${selectedQuestions.length})少于预期题目数(${totalExpectedQuestions})`);
        }
        
        setQuestions(selectedQuestions);
        
        // 初始化答案对象
        if (selectedQuestions.length > 0) {
          const initialAnswers = {};
          selectedQuestions.forEach(q => {
            initialAnswers[getQuestionId(q)] = '';
          });
          setAnswers(initialAnswers);
        } else {
          setQuestions([]);
          setAnswers({});
        }
        
      } catch (error) {
        console.error('Error fetching subject questions:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubjectQuestions();
  }, [examId, subjectId, session, navigate]);

  // 记录每道题的答题时间
  useEffect(() => {
    if (loading) return;
    
    // 创建一个当前状态的快照，避免依赖循环
    const currentQuestionStartTime = questionStartTime;
    const currentQuestionIndex = currentIndex;
    const currentQuestionsData = [...questions];
    
    // 记录时间的函数
    const recordTime = () => {
      const currentTime = Date.now();
      const questionId = currentQuestionsData[currentQuestionIndex] ? 
        getQuestionId(currentQuestionsData[currentQuestionIndex]) : null;
      
      if (questionId && currentQuestionStartTime) {
        const timeSpent = Math.floor((currentTime - currentQuestionStartTime) / 1000); // 秒为单位
        
        if (timeSpent > 0) { // 只在时间有效时更新
          setQuestionTimes(prev => ({
            ...prev,
            [questionId]: (prev[questionId] || 0) + timeSpent
          }));
        }
      }
      
      // 更新开始时间
      setQuestionStartTime(Date.now());
    };
    
    // 初始记录
    setQuestionStartTime(Date.now());
    
    return () => {
      // 组件卸载或依赖变化时记录时间
      recordTime();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, loading]); // 只依赖索引和加载状态

  // 倒计时定时器
  useEffect(() => {
    if (loading) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [loading, handleSubmit]);

  const getQuestionId = (question) => {
    return question._id || question.id;
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleShowSummary = () => {
    setShowSummary(!showSummary);
  };

  if (loading && questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // 如果没有题目
  if (!loading && questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header 
          title={subject?.name || "科目考试"} 
          showBack={true}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-5">
            <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">未找到题目</h2>
            <p className="text-gray-600 mb-4">该科目下没有可用的考试题目</p>
            <button
              onClick={() => navigate(`/exam-start/${examId}`)}
              className="bg-primary text-white px-4 py-2 rounded-lg"
            >
              返回考试页面
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const currentQuestionId = currentQuestion ? getQuestionId(currentQuestion) : null;
  const currentAnswer = currentQuestionId ? answers[currentQuestionId] || '' : '';
  const options = currentQuestion?.options || [];
  
  // 统计已回答题目数量
  const answeredCount = Object.values(answers).filter(a => a !== '').length;

  return (
    <div className="min-h-screen flex flex-col app-bg-color">
      <Header 
        title={subject?.name || "科目考试"} 
        rightContent={
          <div className="flex items-center space-x-2">
            <div className={`bg-white py-1 px-3 rounded-full text-sm font-medium shadow flex items-center ${
              getTimerColor(timeLeft)
            }`}>
              <Clock className="w-4 h-4 mr-1" />
              <span className="inline-block min-w-[35px] text-right">{formatTime(timeLeft)}</span>
            </div>
            <button
              onClick={handleShowSummary}
              className="bg-white p-1 rounded-full shadow"
            >
              <ClipboardList className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        } 
      />
      
      {/* 时间进度条 */}
      <div className="w-full h-2 bg-gray-200 relative">
        <div 
          className={`h-full absolute left-0 top-0 transition-all duration-1000 ${
            timeLeft < 60 ? 'bg-red-600' : 
            timeLeft < 300 ? 'bg-red-500' : 
            timeLeft < 600 ? 'bg-yellow-500' : 
            'bg-primary'
          }`} 
          style={{ width: `${getTimeProgress()}%` }}
        />
      </div>
      
      {/* 大型倒计时显示 - 当剩余时间少于10分钟时显示 */}
      {timeLeft < 600 && (
        <div className={`w-full py-2 text-center ${
          timeLeft < 60 ? 'bg-red-100' : 
          timeLeft < 300 ? 'bg-red-50' :
          'bg-yellow-50'
        }`}>
          <div className={`font-bold ${getTimerColor(timeLeft)}`}>
            剩余时间：{formatTime(timeLeft)}
          </div>
        </div>
      )}
      
      <div className="flex-1 p-4 pb-20">
        {/* 题目答题区域 */}
        {!showSummary && (
          <div className="bg-white">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-500 flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                题目 {currentIndex + 1}/{questions.length}
              </div>
              <div className="text-sm text-primary-dark font-medium flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                已答: {answeredCount}/{questions.length}
              </div>
            </div>
            
            {/* 题目标题行，题型标签直接内联在题目文本前 */}
            <div className="mb-6">
              <div className="bg-gray-50 p-3 border-l-4 border-blue-500 mb-4">
                {currentQuestion?.typename && (
                  <span className={`inline-flex items-center align-middle px-2 py-0.5 text-xs mr-2 ${
                    currentQuestion.typename === '单选' || currentQuestion.typename === '1' ? 'bg-blue-100 text-blue-600' :
                    currentQuestion.typename === '判断' || currentQuestion.typename === '2' ? 'bg-green-100 text-green-600' :
                    currentQuestion.typename === '多选' || currentQuestion.typename === '3' ? 'bg-purple-100 text-purple-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {getTypeName(currentQuestion.typecode)}
                  </span>
                )}
                <span className="font-bold">{currentIndex + 1}.</span> {currentQuestion?.title}
              </div>
              
              {currentQuestion?.content && (
                <div className="text-gray-600 mb-4 pl-3">
                  {currentQuestion.content}
                </div>
              )}
              
              {/* 选项列表 */}
              <div className="mt-5">
                <div className="text-sm text-gray-500 mb-2 pl-2">选择答案：</div>
                <div className="space-y-2">
                  {options.map((option) => (
                    <div 
                      key={option.code}
                      onClick={() => handleAnswer(currentQuestionId, option.code)}
                      className={`p-3 border rounded-lg ${
                        currentAnswer === option.code 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'border-gray-200 hover:bg-gray-50'
                      } cursor-pointer`}
                    >
                      <div className="flex items-start">
                        <div 
                          className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center mr-3 ${
                            currentAnswer === option.code 
                              ? 'bg-blue-500 text-white font-bold' 
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {option.code}
                        </div>
                        <div className="flex-1 text-gray-700 pt-1">
                          {option.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 答题概览 */}
        {showSummary && (
          <div className="bg-white pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">答题概览</h3>
              <div className="text-sm text-primary-dark font-medium flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                已答: {answeredCount}/{questions.length}
              </div>
            </div>
            
            {/* 按题型分组显示题目 */}
            <div className="space-y-4 mb-4">
              {/* 获取所有题型 */}
              {Object.entries(questions.reduce((acc, q) => {
                const type = q.typename || '未知';
                if (!acc[type]) {
                  acc[type] = [];
                }
                acc[type].push(q);
                return acc;
              }, {})).map(([type, typeQuestions]) => {
                // 获取题型样式
                let bgColor, textColor, borderColor;
                if (type === '单选' || type === '1') {
                  bgColor = 'bg-blue-50';
                  textColor = 'text-blue-600';
                  borderColor = 'border-blue-200';
                } else if (type === '判断' || type === '2') {
                  bgColor = 'bg-green-50';
                  textColor = 'text-green-600';
                  borderColor = 'border-green-200';
                } else if (type === '多选' || type === '3') {
                  bgColor = 'bg-purple-50';
                  textColor = 'text-purple-600';
                  borderColor = 'border-purple-200';
                } else {
                  bgColor = 'bg-gray-50';
                  textColor = 'text-gray-600';
                  borderColor = 'border-gray-200';
                }
                
                return (
                  <div key={type} className="rounded-md overflow-hidden border border-gray-200">
                    <div className={`${bgColor} ${textColor} px-3 py-2 font-medium text-sm border-b ${borderColor}`}>
                      {getTypeName(type)} ({typeQuestions.length}题)
                    </div>
                    <div className="p-3">
                      <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                        {typeQuestions.map((question, typeIndex) => {
                          const qId = getQuestionId(question);
                          const isAnswered = answers[qId] && answers[qId] !== '';
                          const questionIndex = questions.findIndex(q => getQuestionId(q) === qId);
                          
                          return (
                            <div 
                              key={qId}
                              onClick={() => {
                                setCurrentIndex(questionIndex);
                                setShowSummary(false);
                              }}
                              className={`h-10 flex items-center justify-center border ${
                                isAnswered 
                                  ? `${bgColor} ${textColor} ${borderColor}` 
                                  : currentIndex === questionIndex
                                    ? 'bg-gray-100 text-gray-800 border-gray-300'
                                    : 'bg-white text-gray-500 border-gray-200'
                              } cursor-pointer`}
                            >
                              <span className="font-medium">{questionIndex + 1}</span>
                              {isAnswered && (
                                <div className="ml-1">
                                  <CheckCircle className="w-3 h-3 text-green-500" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={answeredCount === 0}
                className={`px-6 py-2 rounded-full font-medium flex items-center ${
                  answeredCount === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white'
                }`}
              >
                {answeredCount === questions.length ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    提交答案
                  </>
                ) : (
                  <>
                    提交答案 ({answeredCount}/{questions.length})
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 底部固定导航栏 */}
      {!showSummary && (
        <div className="fixed bottom-0 bg-white border-t border-gray-200 flex justify-between items-center px-4 py-3 z-10 w-full max-w-[450px]">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`px-4 py-2 flex items-center ${
              currentIndex === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700'
            }`}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            上一题
          </button>
          
          {currentIndex < questions.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2 rounded-full bg-blue-600 text-white flex items-center"
            >
              下一题
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-6 py-2 rounded-full bg-green-600 text-white flex items-center"
            >
              提交答案
              <Check className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      )}
      
      {/* 最后一分钟的警告栏 */}
      {timeLeft < 60 && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-600 text-white py-3 text-center font-bold animate-pulse z-50">
          <span className="text-lg">注意！考试时间即将结束，仅剩 {timeLeft} 秒</span>
        </div>
      )}
    </div>
  );
}

export default SubjectExam;